import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import "./ActionCard.css";

// MUI
import { Card, CardContent, CardHeader, Checkbox, Collapse, Skeleton, Stack } from "@mui/material";

// Items
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

const CheckboxCard = (props) => {
	const { label = "label", checked = false, disabled = false, loading = false, onActionChange = () => {} } = props;

	if (loading) {
		return <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: "2px", margin: "5px 12px 2px 0px" }} />;
	}

	return (
		<Checkbox
			edge="start"
			className={clsx({
				"!text-primary": checked,
				"checkbox-card-loading": loading,
			})}
			checked={checked}
			disabled={disabled}
			tabIndex={-1}
			disableRipple
			inputProps={{ "aria-labelledby": label }}
			onChange={(e) => onActionChange?.(e)}
		/>
	);
};
CheckboxCard.propTypes = {
	loading: PropTypes.bool,
	checked: PropTypes.bool,
	disabled: PropTypes.bool,
	label: PropTypes.string,
	onActionChange: PropTypes.func,
};

const GenericCardComponent = (props) => {
	const { children, isAvailable = false, loading = false, variant, width = 30, height = 30 } = props;

	if (!isAvailable) {
		return null;
	}

	if (loading) {
		return <Skeleton variant={variant} width={width} height={height} />;
	}

	return <>{children}</>;
};
GenericCardComponent.propTypes = {
	children: PropTypes.any,
	isAvailable: PropTypes.bool,
	loading: PropTypes.bool,
	variant: PropTypes.string,
	width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const CardContentExpandible = (props) => {
	const {
		children,
		isAvailable = false,
		isExpandable = false,
		checkbox = false,
		expanded = false,
		loading = false,
		onActionExpand = () => {},
	} = props;

	if (!isAvailable) {
		return null;
	}

	if (loading) {
		return (
			<>
				<Skeleton width={"85%"} height={12} />
				<Skeleton width={"85%"} height={12} />
			</>
		);
	}

	if (isExpandable) {
		return (
			<Collapse
				in={expanded}
				timeout="auto"
				collapsedSize={40}
				onClick={(e) => {
					if (checkbox) {
						e.preventDefault();
						e.stopPropagation();
					}
					onActionExpand?.(!expanded);
				}}
			>
				{children}
				{!expanded && (
					<Stack className="action-card-content-dots">
						<MoreHorizIcon />
					</Stack>
				)}
			</Collapse>
		);
	}

	return <>{children}</>;
};
CardContentExpandible.propTypes = {
	children: PropTypes.any,
	isAvailable: PropTypes.bool,
	isExpandable: PropTypes.bool,
	expanded: PropTypes.bool,
	checkbox: PropTypes.bool,
	loading: PropTypes.bool,
	onActionExpand: PropTypes.func,
};

export default function ActionCard(props) {
	const {
		action,
		subheader,
		content,
		contextLength,
		loading = false,
		checkbox = false,
		checked = false,
		disabled = false,
		label = "label",
		title = "",
		maxText = 100,
		skeletonTitleWidth = 150,
		classes = "",
		sx = null,
		onChange = () => {},
	} = props;

	const [expanded, setExpanded] = useState(false);
	const [maxLength, setMaxLength] = useState(100);

	const isContentExpandable = () => contextLength && contextLength >= maxLength;
	const noContent = !subheader || !content;

	useEffect(() => {
		setMaxLength(maxText);
		return () => {
			setMaxLength(100);
		};
	}, [maxText]);

	return (
		<Card
			className={clsx({
				"action-card card-center-head-action": true,
				"action-card-loading": loading,
				"!shadow-soft-widget": true,
				"mb-1": noContent,
				[classes]: classes,
			})}
			sx={sx}
		>
			<CardHeader
				className={clsx({
					"[&>.MuiCardHeader-avatar]:!mr-0 card-header-no-padding-bottom": true,
					"mb-1": noContent && loading,
				})}
				avatar={
					<>
						{checkbox && (
							<CheckboxCard
								loading={loading}
								checked={checked}
								disabled={disabled}
								label={label}
								onActionChange={(e) => onChange?.(e)}
							/>
						)}
					</>
				}
				action={
					<GenericCardComponent isAvailable={Boolean(action)} loading={loading} variant="circular">
						{action}
					</GenericCardComponent>
				}
				title={
					<GenericCardComponent isAvailable={Boolean(title)} loading={loading} width={skeletonTitleWidth} height={20}>
						{title}
					</GenericCardComponent>
				}
				subheader={
					<GenericCardComponent isAvailable={Boolean(subheader)} loading={loading} width={"85%"} height={15}>
						{subheader}
					</GenericCardComponent>
				}
			/>

			{content && (
				<CardContent
					className={clsx({
						"action-card-content": true,
						"checkbox-card": checkbox,
					})}
					sx={{ position: "relative" }}
				>
					<CardContentExpandible
						isAvailable={Boolean(content)}
						isExpandable={isContentExpandable()}
						expanded={expanded}
						checkbox={checkbox}
						loading={loading}
						onActionExpand={(value) => setExpanded(value)}
					>
						{content}
					</CardContentExpandible>
				</CardContent>
			)}
		</Card>
	);
}
ActionCard.propTypes = {
	loading: PropTypes.bool,
	checkbox: PropTypes.bool,
	checked: PropTypes.bool,
	disabled: PropTypes.bool,
	label: PropTypes.string,
	action: PropTypes.any,
	title: PropTypes.any,
	subheader: PropTypes.any,
	content: PropTypes.any,
	maxText: PropTypes.number,
	contextLength: PropTypes.number,
	skeletonTitleWidth: PropTypes.number,
	classes: PropTypes.string,
	sx: PropTypes.object,
	onChange: PropTypes.func,
};
